import { PlanStatus } from "../../../../utils/constants";

/** Meridian Tag `type` values (see @amzn/meridian/tag propTypes). */
export type PlanStatusTagType = "theme" | "neutral" | "success" | "warning" | "error";

export interface PlanStatusTag {
  label: string;
  type: PlanStatusTagType;
}

/**
 * How each plan status reads on the summary page. The raw enum values are
 * backend-shaped (PENDING_USER_INPUT), so they're mapped to operator-facing
 * wording and a colour that matches what the status means for them:
 *   success — the plan is settled and in effect
 *   warning — the plan is waiting on someone
 *   error   — the plan will not be used
 *   neutral — terminal but unremarkable (a past shift, a cancelled draft)
 */
const PLAN_STATUS_TAGS: Record<string, PlanStatusTag> = {
  [PlanStatus.APPROVED]: { label: "Approved", type: "success" },
  [PlanStatus.PENDING_APPROVAL]: { label: "Pending approval", type: "warning" },
  [PlanStatus.PENDING_USER_INPUT]: { label: "Pending inputs", type: "warning" },
  [PlanStatus.RECEIVED_USER_INPUT]: { label: "Inputs received", type: "warning" },
  [PlanStatus.PENDING_CREATION]: { label: "Generating", type: "warning" },
  [PlanStatus.FAILED]: { label: "Failed", type: "error" },
  [PlanStatus.REJECTED]: { label: "Rejected", type: "error" },
  [PlanStatus.CANCELLED]: { label: "Cancelled", type: "neutral" },
  // A past shift's plan. It was the plan of record, so it isn't an error state —
  // there is just nothing left to do with it.
  [PlanStatus.CLOSED]: { label: "Closed", type: "neutral" },
};

/**
 * The status tag to show for a plan, or null when there is nothing meaningful to
 * show — an absent status, or a status this frontend doesn't recognise (a new
 * backend value shouldn't surface a raw enum token to an operator).
 *
 * `approvedThisSession` covers plans published from this page: the plan list
 * belongs to the parent and isn't refetched on publish, so its status stays
 * PENDING_APPROVAL even though the plan is now approved.
 */
export const getPlanStatusTag = (
  status?: string | null,
  approvedThisSession = false
): PlanStatusTag | null => {
  if (approvedThisSession) {
    return PLAN_STATUS_TAGS[PlanStatus.APPROVED];
  }
  if (!status) {
    return null;
  }
  return PLAN_STATUS_TAGS[status] ?? null;
};
